// FILE: src/pages/AdminNursingHomes.jsx
// Super admin: create and list nursing home tenants.
import React, { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import SuperAdminNav from "../components/SuperAdminNav";

const FN_BASE = "https://us-central1-lastwishbox-business.cloudfunctions.net";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#fff",
  border: "#e8ebf3",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${B.border}`,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

async function token() {
  const u = getAuth().currentUser;
  if (!u) throw new Error("Not signed in");
  return u.getIdToken();
}

function fmtDate(ms) {
  if (!ms) return "\u2014";
  try { return new Date(ms).toLocaleDateString("en-IE"); } catch { return "\u2014"; }
}

export default function AdminNursingHomes() {
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", ownerEmail: "", reminderEmail: "", refCode: "" });
  const [codeTouched, setCodeTouched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const t = await token();
      const r = await fetch(`${FN_BASE}/nhListHomes`, { headers: { Authorization: `Bearer ${t}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
      setHomes(Array.isArray(d.homes) ? d.homes : []);
    } catch (e) {
      console.error("nhListHomes failed", e);
      setErr(String(e.message || e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Suggest a code from the name until the admin edits the code themselves.
  useEffect(() => {
    if (codeTouched || !form.name.trim()) return;
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        const t = await token();
        const r = await fetch(`${FN_BASE}/nhSuggestCode?name=${encodeURIComponent(form.name)}`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        const d = await r.json().catch(() => ({}));
        if (!ignore && r.ok && d.code) setForm((s) => ({ ...s, refCode: d.code }));
      } catch { /* suggestion is optional */ }
    }, 500);
    return () => { ignore = true; clearTimeout(timer); };
  }, [form.name, codeTouched]);

  const create = async () => {
    setSaving(true);
    setErr("");
    setOk("");
    try {
      const t = await token();
      const r = await fetch(`${FN_BASE}/nhCreateHome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);

      setOk(
        `${form.name} created. Code ${d.refCode}. ` +
        (d.inviteSent ? "Invite email sent to the owner." : "Invite email FAILED - send credentials manually.") +
        (d.ownerExisted ? " Note: that email already had an account, it has been linked." : "")
      );
      setForm({ name: "", ownerEmail: "", reminderEmail: "", refCode: "" });
      setCodeTouched(false);
      setShowForm(false);
      await load();
    } catch (e) {
      console.error("nhCreateHome failed", e);
      setErr(String(e.message || e));
    }
    setSaving(false);
  };

  const canSave = form.name.trim() && form.ownerEmail.includes("@") && form.refCode.trim();

  return (
    <div className="ad ad-wrap">
      <SuperAdminNav title="Admin" active="nursinghomes" />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: B.redDark }}>Nursing Homes</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)}
            style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: B.orange, color: B.white, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
          >Add a home</button>
        )}
      </div>

      {err && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{err}</div>}
      {ok && <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{ok}</div>}

      {showForm && (
        <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 16px", fontSize: 17, color: B.redDark }}>New nursing home</h2>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Home name</label>
            <input style={inputStyle} value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Owner login email</label>
            <input style={inputStyle} value={form.ownerEmail}
              onChange={(e) => setForm((s) => ({ ...s, ownerEmail: e.target.value }))} />
            <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
              This person gets an invite to set their password. They can then invite their own staff.
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Reminder email</label>
            <input style={inputStyle} value={form.reminderEmail} placeholder="Leave blank to use the owner email"
              onChange={(e) => setForm((s) => ({ ...s, reminderEmail: e.target.value }))} />
            <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
              Where review reminders are sent. Can be a shared inbox.
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Referral code</label>
            <input style={{ ...inputStyle, letterSpacing: 2, fontWeight: 700 }} value={form.refCode}
              onChange={(e) => { setCodeTouched(true); setForm((s) => ({ ...s, refCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })); }} />
            <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
              Residents enter this when they sign up. Letters and numbers only.
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={() => { setShowForm(false); setCodeTouched(false); }}
              style={{ padding: "9px 18px", borderRadius: 10, border: `1px solid ${B.border}`, background: B.white, color: B.steel, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >Cancel</button>
            <button onClick={create} disabled={saving || !canSave}
              style={{
                padding: "9px 20px", borderRadius: 10, border: "none", background: B.orange, color: B.white,
                fontWeight: 700, fontSize: 14,
                cursor: saving || !canSave ? "default" : "pointer",
                opacity: saving || !canSave ? .6 : 1,
              }}>{saving ? "Creating\u2026" : "Create home"}</button>
          </div>
        </div>
      )}

      <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 16, color: B.redDark }}>All homes</h2>
          <span style={{ fontSize: 13, color: B.steel }}>{homes.length} home{homes.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: B.steel }}>Loading&hellip;</div>
        ) : homes.length === 0 ? (
          <div style={{ padding: 28, textAlign: "center", color: B.steel }}>No nursing homes yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: B.bg }}>
                  {["Home", "Code", "Residents", "Consent", "Reviews due", "Staff", "Created"].map((h) => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, color: B.redDark, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {homes.map((h, i) => (
                  <tr key={h.tenantId} style={{ borderTop: `1px solid ${B.border}`, background: i % 2 === 0 ? B.white : "#fafafa" }}>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ fontWeight: 700, color: B.redDark }}>{h.name || "\u2014"}</div>
                      <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>{h.contactEmail || ""}</div>
                    </td>
                    <td style={{ padding: "11px 14px", fontWeight: 700, letterSpacing: 1, color: B.navy }}>{h.refCode || "\u2014"}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ fontWeight: 700, color: B.navy }}>{h.residentCount}</div>
                      <div style={{ fontSize: 12, color: B.steel }}>{h.activeCount} activated</div>
                    </td>
                    <td style={{ padding: "11px 14px", color: h.consentCount < h.residentCount ? B.red : "#065f46", fontWeight: 700 }}>
                      {h.consentCount}/{h.residentCount}
                    </td>
                    <td style={{ padding: "11px 14px", fontWeight: 700, color: h.reviewsDue > 0 ? B.red : B.steel }}>{h.reviewsDue}</td>
                    <td style={{ padding: "11px 14px", color: B.steel }}>{h.staffCount}</td>
                    <td style={{ padding: "11px 14px", color: B.steel, fontSize: 13 }}>{fmtDate(h.createdAtMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
        .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      `}</style>
    </div>
  );
}
